import type { WidgetProps } from '@rjsf/utils';
import { ariaDescribedByIds } from '@rjsf/utils';
import React, { SetStateAction, useEffect, useMemo, useState } from 'react';
import { useCallback } from 'react';

import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/ui/Select';

import { Wrapper } from '../shared';

interface ISelectWidget<T, S, F> extends Omit<WidgetProps<T, S, F>, 'value'> {
	value: string;
}

const Widget = <T, S, F>({
	disabled,
	formContext = {} as F,
	id,
	onChange,
	options,
	placeholder,
	readonly,
	value = '',
	name,
	registry,
	schema,
	uiSchema,
	baseValuesKey
}: ISelectWidget<T, S, F>) => {
	const currentValue = value && options?.enumOptions ? options.enumOptions.find((option) => `${option.value}` === `${value}`) : undefined;
	const [baseValue, setBaseValue] = useState<string>(currentValue ?
		currentValue.schema?.parentId?.toString() || ''
			:
		''
	);
	const { readonlyAsDisabled = true } = formContext as F & { readonlyAsDisabled: boolean };

	const enumOptionsBase = registry?.rootSchema?.definitions?.[baseValuesKey]?.oneOf ?? [];
	const enumOptions = useMemo(() => {
		return (options?.enumOptions ?? []).filter(({ schema }) => schema?.parentId.toString() === baseValue) ?? [];
	}, [baseValue]);

	useEffect(() => {
		if (enumOptions.length === 1) {
			handleChange(enumOptions[0].value)
		} else {
			handleChange(null);
		}
	}, [enumOptions]);

	const handleChange = useCallback((nextValue: SetStateAction<string>) => {
		if (!nextValue) {
			nextValue = null;
		}
		if (nextValue || nextValue === null) {
			if (schema?.type === 'number') {
				onChange(nextValue === null ? schema.default : +nextValue || schema.default);

				return;
			}

			onChange(nextValue === null ? schema.default : nextValue || schema.default);
		}
	}, []);

	const handleChangeBase = useCallback((value) => {
		setBaseValue(value);
	}, []);

	return (
		<div className='flex flex-col gap-2'>
			<Select
				onValueChange={!readonly ? handleChangeBase : undefined}
				disabled={disabled || (readonlyAsDisabled && readonly) || !enumOptionsBase.length}
				aria-describedby={ariaDescribedByIds<T>(id)}
				value={baseValue}
				name={name}
			>
				<SelectTrigger className="w-full">
					<SelectValue placeholder={uiSchema?.['ui:placeholderBase'] || placeholder} />
				</SelectTrigger>

				<SelectContent>
					<SelectGroup>
						{enumOptionsBase.map(option => (
							<SelectItem key={`option-${option.const}`} value={option.const.toString()}>
								{option.title}
							</SelectItem>
						))}
					</SelectGroup>
				</SelectContent>
			</Select>
			{baseValue && enumOptions.length > 1 &&
				<Select
					onValueChange={!readonly ? handleChange : undefined}
					disabled={disabled || (readonlyAsDisabled && readonly) || !enumOptions.length}
					aria-describedby={ariaDescribedByIds<T>(id)}
					value={value}
					name={name}
				>
					<SelectTrigger className="w-full">
						<SelectValue placeholder={placeholder} />
					</SelectTrigger>

					<SelectContent>
						<SelectGroup>
							{enumOptions.map(({ value, label }) => (
								<SelectItem key={`option-${value}`} value={value} className={'w-[850px] [&:not(:last-child)]:border-b-[1px] border-brand-gray-4'}>
									{label}
								</SelectItem>
							))}
						</SelectGroup>
					</SelectContent>
				</Select>
			}
		</div>
	);
};

const NationalTargetsWidget = (baseValuesKey: string) => {
	return (props) => {
		const { value, onChange } = props;
		return <Wrapper value={value} onChange={onChange}>
			<Widget baseValuesKey={baseValuesKey} {...props} />
		</Wrapper>
	}
};

export { NationalTargetsWidget };
